import { useEffect, useMemo, useRef, useState } from 'react';
import * as venn from '@upsetjs/venn.js';
import * as d3 from 'd3';
import { getColor, variantByIndex } from '@/helpers/utils';
import { useDispatch } from 'react-redux';
import { Form } from 'react-bootstrap';
import numberHumanizer from '@/helpers/numberHumanizer';
import countSuffix from '@/helpers/countSuffix';
import { showDrawer } from '@/app/common/reducers/drawer';
import type { GapHostItem } from '@/app/KeywordResearch/components/keyword-gap/hostApiFormatter';

export type VennDiagramEntry = { sets: string[]; size: number };

type VennDiagramProps = {
  data: VennDiagramEntry[] | undefined;
  submittedHosts: GapHostItem[];
};

const VennDiagram = ({ data, submittedHosts }: VennDiagramProps) => {
  const vennRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const [activeDomains, setActiveDomains] = useState<string[]>(
    submittedHosts.map(({ host }) => host)
  );

  useEffect(() => {
    setActiveDomains(submittedHosts.map(({ host }) => host));
  }, [data, submittedHosts]);

  const toggleDomain = (domain: string) => {
    setActiveDomains(prev =>
      prev.includes(domain) ? prev.filter(d => d !== domain) : [...prev, domain]
    );
  };

  const filteredData = useMemo(
    () =>
      (data ?? []).filter(entry =>
        entry.sets.every(set => activeDomains.includes(set))
      ),
    [data, activeDomains]
  );

  useEffect(() => {
    const updateChartSize = () => {
      if (!vennRef.current) return;

      const containerWidth =
        vennRef.current.clientWidth || window.innerWidth * 0.8;

      d3.select(vennRef.current).selectAll('*').remove();

      const hostIndexMap = Object.fromEntries(
        submittedHosts.filter(({ host }) => host).map(({ host, i }) => [host, i])
      );
      const getSetColor = (sets: string[]) => {
        const indices = sets
          .map(host => hostIndexMap[host])
          .filter(i => i !== undefined);

        if (indices.length === 0) {
          return getColor(variantByIndex(0));
        }

        return getColor(variantByIndex(indices[0]));
      };

      const colorScheme = filteredData
        .filter(entry => entry.size !== 0)
        .map(entry => getSetColor(entry.sets));

      const chart = venn
        .VennDiagram({
          textFill: getColor('black'),
          symmetricalTextCentre: true,
          colorScheme
        })
        .width(containerWidth)
        .height(280);

      const div = d3.select(vennRef.current);
      div.datum(filteredData).call(chart);

      div.selectAll('g.venn-area text').each(function () {
        const textElement = d3.select(this);
        textElement.selectAll('tspan').text('');
      });

      div
        .selectAll<SVGGElement, VennDiagramEntry>('g.venn-area')
        .on('mouseover', function (e: MouseEvent, d) {
          const eventVen = d3.select(
            `g[data-venn-sets="${d?.sets?.join('_')}"] path`
          );
          if (d.sets.length > 1) {
            eventVen?.style('fill', getColor('gray-600'));
          }
          eventVen
            ?.transition()
            ?.duration(100)
            ?.style('transform', 'scale(1.02)')
            ?.style('cursor', 'pointer')
            ?.style('fill-opacity', 0.75);
          d3.select('.d3-tooltip')?.transition()?.duration(200);
          d3.select('.d3-tooltip')
            ?.style('opacity', 1)
            ?.style('border', `1px solid black`)
            ?.html(
              `<div>
             
            ${
              d.sets.length === 1
                ? `
              <div>تمام کلمات</div>  
              <hr class='m-2'/>
              <div class='d-flex align-items-center gap-1'>
                <div class='mt-n1' style="background-color:${eventVen?.style('fill')};width:12px;height:12px;border-radius:9999px;"></div>
                <span>${d.sets[0]}</span>&nbsp;&nbsp;&nbsp;${numberHumanizer(d.size)}
              </div>`
                : `
              <div>مشترک&nbsp;&nbsp;&nbsp;${d.size}</div>  
              <hr class='m-2'/>
              ${d.sets
                .map(item => {
                  return `<div class='d-flex align-items-center gap-1'>
                <div class='mt-n1' style="background-color:${getColor(variantByIndex(hostIndexMap[item]))};width:12px;height:12px;border-radius:9999px;"></div>
                <span>${item}</span>&nbsp;&nbsp;&nbsp;${numberHumanizer(
                  data?.find(({ sets }) => sets[0] === item)?.size || ''
                )}
                </div>`;
                })
                .join('')}`
            }
            
        </div>`
            )
            .style('left', e.clientX - 180 + 'px')
            .style('top', e.clientY - 0 + 'px');
        })
        .on('mousemove', function (e: MouseEvent, d) {
          const eventVen = d3.select(
            `g[data-venn-sets="${d?.sets?.join('_')}"] path`
          );
          if (d.sets.length > 1) {
            eventVen?.style('fill', '');
          }
          d3.select('.d3-tooltip')
            .style('opacity', 1)
            .style('left', e.clientX - 180 + 'px')
            .style('top', e.clientY - 0 + 'px');
        })
        .on('mouseleave', function (e: MouseEvent, d) {
          const eventVen = d3.select(
            `g[data-venn-sets="${d?.sets?.join('_')}"] path`
          );
          eventVen
            ?.transition()
            ?.duration(100)
            ?.style('transform', 'scale(1)')
            ?.style('fill-opacity', d?.sets?.length === 1 ? 0.5 : 0);
          (e.target as Element).classList.remove('opacity-100');
          d3.select('.d3-tooltip').transition().duration(200).style('opacity', 0);
        })
        .on('click', function (_e, d) {
          dispatch(
            showDrawer({
              key: 'venn',
              payload: {
                set: d?.sets.join(',')
              }
            })
          );
        });

      div
        .selectAll('.venn-circle path')
        .style('stroke', function () {
          return d3.select(this).style('fill');
        })
        .style('stroke-width', 2)
        .style('transform-origin', 'center')
        .style('fill-opacity', 0.5);

      div
        .selectAll('.venn-intersection path')
        .style('stroke', '#fff')
        .style('stroke-width', 1)
        .style('transform-origin', 'center')
        .style('fill-opacity', 0);
    };

    updateChartSize();
    window.addEventListener('resize', updateChartSize);
    return () => {
      window.removeEventListener('resize', updateChartSize);
    };
  }, [filteredData, submittedHosts, data, dispatch]);

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex flex-wrap gap-2">
        {submittedHosts.map(({ host, i }) => {
          if (!host) return null;
          const active = activeDomains.includes(host);
          const color = getColor(variantByIndex(i));
          const inputId = `venn-domain-${i}`;
          const size = data?.find(
            ({ sets }) => sets.length === 1 && sets[0] === host
          )?.size;
          return (
            <div
              key={host}
              className="d-inline-flex align-items-center gap-2 border rounded-pill px-2 py-1"
              style={{ opacity: active ? 1 : 0.45 }}
            >
              <Form.Check.Input
                onChange={() => toggleDomain(host)}
                type="checkbox"
                id={inputId}
                checked={active}
                style={{
                  borderColor: color,
                  backgroundColor: active ? color : 'transparent',
                  marginBottom: '-3px'
                }}
                className="dot mt-0 shadow-none remove-checked-icon rounded-circle cursor-pointer"
              />
              <Form.Check.Label
                htmlFor={inputId}
                className="fs--1 mb-0 text-900 cursor-pointer text-nowrap"
              >
                {host}
                <span className="text-600 ms-1">{countSuffix(size)}</span>
              </Form.Check.Label>
            </div>
          );
        })}
      </div>
      <div>
        <div className="d3-tooltip"></div>
        <div ref={vennRef} style={{ width: '100%', height: 280 }}></div>
      </div>
      <p className="text-600 fs--2 mb-0">
        برای دیدن کلمات، روی هر بخش نمودار کلیک کن.
      </p>
    </div>
  );
};

export default VennDiagram;
